// Connect copy of sidekick-web's MeetingSummary: just the summary tab the
// call page uses. Meeting-only cards (notes, emailed, bookmarks) are left out.
import { apiPlaceholder } from "../../utils/meetingFormat";
import { SentimentChip } from "../records/RecordList";
import { Card, Icon, LinkButton, SectionLabel, Spinner, ui } from "../ui";

const body = { margin: 0, fontSize: "14px", lineHeight: 1.6 };

// Calls can come back with notes but no summary paragraph, so they pass
// hasSummary themselves
export function SummaryTab({
  meeting,
  loading,
  canResummarise,
  onResummarise,
  hasSummary = !!meeting.summary,
  subject = "meeting",
  emptyText = `There's no summary for this ${subject}. The transcript may be too short.`,
}) {
  const writing = meeting.status.value === "SUMMARISING";
  const notes = meeting.meeting_notes ?? [];

  const resummariseLink = canResummarise && (
    <LinkButton onClick={onResummarise}>
      <Icon name="refresh" size={12} />
      {meeting.summary ? "Re-summarise with another template" : "Summarise"}
    </LinkButton>
  );

  if (!hasSummary) {
    return (
      <Card gap={10} style={{ alignItems: "flex-start" }}>
        {(writing || loading) && <Spinner />}
        {!loading && (
          <p style={{ ...body, ...ui.muted }}>
            {writing
              ? "Writing the summary and notes. This updates by itself."
              : emptyText}
          </p>
        )}
      </Card>
    );
  }

  const sentimentLine = meeting.sentiment && (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        flexWrap: "wrap",
      }}
    >
      <SentimentChip
        sentiment={{ rating: meeting.sentiment.rating, summary: null }}
        neutralIfUnknown
      />
      {meeting.sentiment.summary &&
        !apiPlaceholder(meeting.sentiment.summary) && (
          <span style={ui.muted}>{meeting.sentiment.summary}</span>
        )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {meeting.summary ? (
        <Card
          title="Summary"
          actions={notes.length === 0 && resummariseLink}
          gap={10}
        >
          <p style={{ ...body, whiteSpace: "pre-line" }}>{meeting.summary}</p>
          {sentimentLine}
        </Card>
      ) : (
        sentimentLine
      )}

      {notes.length > 0 ? (
        <Card title="Notes" actions={resummariseLink} gap={10}>
          {notes.map((note, i) => (
            <NoteSection key={i} note={note} />
          ))}
        </Card>
      ) : (
        loading && <Spinner />
      )}
    </div>
  );
}

// A lone line reads as a paragraph, otherwise bullets (as MeetingNotesList)
function NoteSection({ note }) {
  const content = (note.content ?? []).filter(Boolean);
  if (!note.header && content.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {note.header && (
        <SectionLabel style={{ marginTop: "6px" }}>{note.header}</SectionLabel>
      )}
      {content.length <= 1 || !note.header ? (
        <p style={body}>{content.join(" ")}</p>
      ) : (
        <ul style={{ ...body, paddingLeft: "18px" }}>
          {content.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
